import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { useI18n } from '../../i18n';
import { useToast } from '../../hooks/useToast';
import { DotIcon } from '../shared/Icons';
import type { Bout } from '../../types';
import type { VerdictFormState } from './BoutCard';
import { BOUT_OUTCOMES } from '../../utils/boutOutcomes';
import { boutRounds } from '../../utils/tournament';
import { fillTemplate } from '../../lib/fillTemplate';
import Notice from '../shared/Notice';
import Field from '../shared/Field';

// Tied-score auto-default (winner='draw', outcome='DRAW' on Challenge bouts)
// used to live here as a ref-guarded useEffect that wrote into verdictForm
// state. It was prone to a stuck-state bug: unchecking a round triggered
// the write, rechecking left the stale 'DRAW' in state masking the
// recomputed 'WP'. The auto-default now lives in BoutCard's render-time
// merge (`tiedAutoOutcome`) — this component only emits the warning toast
// and enforces the submit-disable policy below.

// #458 — the one outcome list (utils/boutOutcomes.ts).
const OUTCOMES = BOUT_OUTCOMES;

// Outcomes that declare a winner by points on the scorecards. A tied total
// with any of these is logically impossible; the verdict must be DRAW (for
// Challenge bouts) or scores must be corrected before submitting.
// WP is the only IFMA decision-by-points code (v3.057 §30.1). Earlier code
// listed 'WPHI' and 'WPHO' here — purged 2026-04-27, not real IFMA codes.
const DECISION_OUTCOMES = new Set(['WP']);

interface VerdictFormProps {
  bout: Bout;
  verdictForm: VerdictFormState;
  setVerdictForm: Dispatch<SetStateAction<VerdictFormState>>;
  onSubmit: (e: React.FormEvent) => void;
  /** #605 — true while the verdict request runs: Submit takes no second tap. */
  submitting?: boolean;
  displayRounds: number[];
  /** #469 — the approved rounds that most judges gave to each athlete. */
  roundsWon?: { red: number; blue: number };
}

export default function VerdictForm({ bout, verdictForm, setVerdictForm, onSubmit, submitting = false, displayRounds, roundsWon }: VerdictFormProps) {
  const { t } = useI18n();
  const { showWarning } = useToast();

  // ── Tied-score detection ─────────────────────────────────────────────────
  // Per IFMA v3.057 §30.8, DRAW is only permitted in Exhibition matches — never
  // in tournament brackets. §29.3 requires a winner to be nominated in all
  // tournament bouts, and gives the tiebreaker hierarchy for the whole bout
  // (fewer infringements is the LAST criterion, not the
  // first). This app currently aggregates into a single jury-approved row,
  // so on tied totals we:
  //   - Challenge (Open Ring) bouts: auto-default to DRAW so the jury can
  //     confirm with one click. They can still override manually.
  //   - Bracket bouts (QF/SF/Final/R16/R32): NEVER auto-default to DRAW —
  //     the backend would reject it anyway. Jury must pick a winner. We
  //     surface an amber toast with the IFMA tiebreak hierarchy reference.
  //   - Non-decision outcomes (KO/RSC/DQ/…) accept tied totals legitimately.
  // A total of 0 is no total, as on the server (#469 reviews).
  const toTotal = (s: string) => (s === '' || Number(s) === 0 ? NaN : Number(s));
  const rS = toTotal(verdictForm.red_corner_score);
  const bS = toTotal(verdictForm.blue_corner_score);
  const bothScoresFilled = !Number.isNaN(rS) && !Number.isNaN(bS);
  // One total alone: the server refuses it with CORNER_SCORES_INCOMPLETE.
  const scoresIncomplete = Number.isNaN(rS) !== Number.isNaN(bS);
  const scoresTied = bothScoresFilled && rS === bS;
  const outcome = (verdictForm.outcome || '').toUpperCase();
  const isDecisionOutcome = DECISION_OUTCOMES.has(outcome);
  const winner = (verdictForm.winner || '').toLowerCase();
  // #317 — the backend computes the DRAW gate and ships it as `draw_eligible`
  // (routes/bouts.ts BOUT_COLUMNS, mirroring routes/verdict.ts). Read it; do
  // NOT re-derive the rule here. This component used to test
  // `stage === 'challenge'`, which offered the jury a DRAW button on a
  // 'Challenge'-labelled bout inside a championship — the submit then failed
  // with 400 DRAW_NOT_ALLOWED_NON_CHALLENGE.
  const isChallenge = bout.draw_eligible === true;
  const tiedAgainstDecision = scoresTied && isDecisionOutcome && winner !== 'draw';
  // Submit-disable policy: block only on Challenge bouts where DRAW is a
  // legitimate alternative the jury can pick. On bracket bouts the jury has
  // already walked the IFMA v3.057 §29.3 tiebreak hierarchy internally — their
  // nomination is final even with tied display totals.
  const blockSubmitForTie = tiedAgainstDecision && isChallenge;
  // #469 — the jury is the final scorer, and the final score is shown with
  // the result: a WP winner must not have fewer points than the opponent
  // (the server refuses it with WP_WINNER_HAS_FEWER_POINTS). Equal points
  // stay the jury's pick.
  const winnerHasFewer = bothScoresFilled && isDecisionOutcome
    && ((winner === 'red' && rS < bS) || (winner === 'blue' && bS < rS));
  const fewerText = t.errors.WP_WINNER_HAS_FEWER_POINTS
    .replace('{redScore}', String(rS)).replace('{blueScore}', String(bS));
  // A draw needs equal totals (operator decision, 2026-09-24): the server
  // refuses it with DRAW_NEEDS_EQUAL_SCORES.
  const drawUnequal = bothScoresFilled && winner === 'draw' && rS !== bS;
  const drawText = t.errors.DRAW_NEEDS_EQUAL_SCORES
    .replace('{redScore}', String(rS)).replace('{blueScore}', String(bS));
  // Equal final scores where a draw is not allowed: the jury picks by the
  // whole bout (IFMA v3.057 §29.3). The rounds won are information only.
  const showTieHelp = scoresTied && isDecisionOutcome && !isChallenge;

  // Backend rejects with RING_REF_REQUIRED_FOR_VERDICT (400) when the bout
  // has no ring referee assigned. Surface that as a render-time gate instead
  // of a 400-toast round-trip — the operator sees the missing dependency
  // before clicking, can scroll up to the Officials section to assign, and
  // skips the see-error → fix → retry loop seen in App Insights.
  const ringRefMissing = !bout.ring_referee_id;

  // ── Tied-score warning toast ─────────────────────────────────────────────
  // Emits a one-shot amber warning per unique tied-state key so the jury
  // doesn't get the same toast twice for the same condition. Correct deps
  // listed — no eslint-disable needed.
  const lastWarnedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tiedAgainstDecision) return;
    const key = `${rS}-${bS}-${outcome}-${isChallenge}`;
    if (lastWarnedKeyRef.current === key) return;
    lastWarnedKeyRef.current = key;
    if (isChallenge) {
      showWarning(t.bout.tiedUseDraw.replace('{redScore}', String(rS)).replace('{blueScore}', String(bS)));
    } else {
      // Bracket bout — DRAW is illegal. Jury must resolve via IFMA v3.057 §29.3
      // criteria (scoring skills → forceful skills → less exhaustion → …  →
      // fewer infringements) and nominate a winner.
      showWarning(t.bout.tiedPickWinner.replace('{redScore}', String(rS)).replace('{blueScore}', String(bS)));
    }
  }, [tiedAgainstDecision, rS, bS, outcome, isChallenge, showWarning, t]);

  return (
    <div className="card p-4">
      <h3 className="font-semibold text-sm text-gray-900 mb-4 flex items-center gap-2">{t.bout.submit_verdict} <span className="badge-purple">{t.bout.jury}</span></h3>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={
            <span className="text-red-600 inline-flex items-center gap-1">
              <DotIcon className="w-5 h-5 text-red-500" /> {t.common.red} {t.common.total}
            </span>}>
            <input type="number" min={0} value={verdictForm.red_corner_score}
              onChange={e => setVerdictForm(f => ({ ...f, red_corner_score: e.target.value }))}
              className="input text-center font-mono text-lg text-red-700 font-bold" />
          </Field>
          <Field label={
            <span className="text-blue-600 inline-flex items-center gap-1">
              <DotIcon className="w-5 h-5 text-blue-500" /> {t.common.blue} {t.common.total}
            </span>}>
            <input type="number" min={0} value={verdictForm.blue_corner_score}
              onChange={e => setVerdictForm(f => ({ ...f, blue_corner_score: e.target.value }))}
              className="input text-center font-mono text-lg text-blue-700 font-bold" />
          </Field>
        </div>
        <Field group label={t.common.winner} required>
          {/* The order of the ring: Red, then DRAW between the corners, then Blue (operator, 2026-10-14). */}
          <div data-testid="verdict-winner-buttons" className={`grid gap-2 ${isChallenge ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {((['red', 'draw', 'blue'] as const).filter(w => w !== 'draw' || isChallenge)).map(w => (
              <button key={w} type="button" onClick={() => setVerdictForm(f => ({ ...f, winner: w, ...(w === 'draw' ? { outcome: 'DRAW' } : {}) }))}
                className={`py-2.5 rounded-lg text-sm font-bold border-2 transition-all ${
                  verdictForm.winner === w
                    ? w === 'red'  ? 'border-red-600  bg-red-600  text-white'
                    : w === 'blue' ? 'border-blue-600 bg-blue-600 text-white'
                    :                'border-gray-700 bg-gray-700 text-white'
                    : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                {t.common[w]}
              </button>
            ))}
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t.common.decision} required>
            <select value={verdictForm.outcome}
              onChange={e => {
                // Pair invariant — picking outcome=DRAW must also flip
                // winner→draw (mirror of the winner-button handler above
                // which sets outcome=DRAW when winner=draw is clicked).
                // Without this, the form can submit
                // {winner:'red', outcome:'DRAW'} which the backend rejects
                // (DRAW_PAIR_INVARIANT). Catching it here gives instant UI
                // feedback without a 400 round-trip.
                // The reverse holds too: a Draw winner leaves with the DRAW
                // outcome, or Draw + KO reaches the server (#469 logic review).
                const v = e.target.value;
                setVerdictForm(f => ({
                  ...f,
                  outcome: v,
                  ...(v === 'DRAW' ? { winner: 'draw' as const }
                    : f.winner === 'draw' ? { winner: '' as const } : {}),
                }));
              }}
              className="input">
              <option value="">— {t.common.select} —</option>
              {OUTCOMES
                // DRAW is only valid on Challenge (Open Ring) bouts per IFMA
                // v3.057 §30.8. Filter it out of the dropdown on bracket bouts so
                // the option doesn't even exist for the jury to pick.
                .filter(o => o.code !== 'DRAW' || isChallenge)
                .map(o => <option key={o.code} value={o.code}>{o.code} — {o.label}</option>)}
            </select>
          </Field>
          <Field label={t.bout.roundEnded}>
            <select value={verdictForm.round_ended}
              onChange={e => setVerdictForm(f => ({ ...f, round_ended: e.target.value }))}
              className="input">
              <option value="">—</option>
              {/* #602 — the rounds of the bout: 3, or 5. */}
              {Array.from({ length: boutRounds(bout) }, (_, i) => i + 1).map(r => (
                <option key={r} value={String(r)}>
                  {t.common.round} {r}{r === boutRounds(bout) ? ` (${t.bout.fullBout})` : ''}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {displayRounds.length > 0 ? (
          <Notice tone="success" size="xs">
            {fillTemplate(t.bout.scoreboardShowsRounds, {
              rounds: displayRounds.map(r => fillTemplate(t.bout.roundN, { n: r }, t)).join(', '),
            }, t)}
          </Notice>
        ) : (
          <Notice tone="warning" size="xs">
            {t.bout.noRoundsApproved}
          </Notice>
        )}
        {winnerHasFewer && <Notice tone="error" size="xs" data-verdict-fewer>{fewerText}</Notice>}
        {drawUnequal && <Notice tone="error" size="xs" data-verdict-draw-unequal>{drawText}</Notice>}
        {scoresIncomplete && <Notice tone="error" size="xs" data-verdict-incomplete>{t.errors.CORNER_SCORES_INCOMPLETE}</Notice>}
        {showTieHelp && (
          <Notice tone="neutral" size="xs" data-verdict-tie>
            <div>{t.bout.tieHelp}</div>
            {roundsWon && (
              <div className="mt-1 font-semibold" data-rounds-won>
                {t.bout.roundsWon.replace('{red}', String(roundsWon.red)).replace('{blue}', String(roundsWon.blue))}
              </div>
            )}
          </Notice>
        )}
        {ringRefMissing && <Notice tone="warning" size="xs">{t.bout.ringRefRequiredHint}</Notice>}
        {/* A 48 px touch target, as the judge's Submit Round: the jury taps
            it on a phone or a tablet at ringside (operator, 2026-09-24;
            code-patterns.md § Button sizing). */}
        <button type="submit"
          disabled={submitting || !verdictForm.winner || !verdictForm.outcome || blockSubmitForTie || winnerHasFewer || drawUnequal || scoresIncomplete || ringRefMissing}
          title={
            ringRefMissing ? t.bout.ringRefRequiredHint
            : blockSubmitForTie ? 'Tied scores cannot produce a points decision on a Challenge bout — change outcome to DRAW or correct scores.'
            : winnerHasFewer ? fewerText
            : drawUnequal ? drawText
            : scoresIncomplete ? t.errors.CORNER_SCORES_INCOMPLETE
            : undefined
          }
          className="btn-primary w-full min-h-12">
          {t.bout.submit_verdict}
        </button>
      </form>
    </div>
  );
}
